import React, { useMemo, Suspense, useEffect, useState } from 'react'
import {
  useLocation,
  matchPath,
  Outlet,
  Link,
  useParams,
  useNavigate,
} from 'react-router-dom'
import {
  pageUrl,
  pageUrlWithoutMargin,
  pageUrlWithoutSidebar,
} from '@/constants/url'
import './auth.scss'
import { Layout, Menu, Select, Avatar, Spin, Typography, Button } from 'antd'
import logo from '@/assets/images/logo.svg'
import logoWithName from '@/assets/images/logo-right-name.svg'
import { useMenu } from '@/hooks/commons/useMenu'
import { useBranchStore } from '@/store/useBranchStore'
import classNames from 'classnames'
import { getInitial } from '@/utils/helpers/formatHelper'
import {
  getBranchLocalStorage,
  getOriginUrlWithoutBranch,
  saveBranch,
} from '@/utils/helpers/urlHelper'
import { MenuOutlined } from '@ant-design/icons'
import { useScreenWidth } from '@/hooks/commons/useScreenWidth'
import { useCommonStore } from '@/store/useCommonStore'

import dayjs from 'dayjs'
import { useGetBranchDetail } from '@/hooks/branches/useGetBranchDetail'
import { TIMEZONE } from '@/constants/common'
import { useTransactionBranchSignal } from '@/hooks/commons/useTransactionBranchSignal'

const { Header, Content, Sider } = Layout
const { Text } = Typography

const remapActive = {
  [getOriginUrlWithoutBranch(pageUrl.bundles)]: getOriginUrlWithoutBranch(
    pageUrl.services,
  ),
}

export const AuthLayout: React.FC = () => {
  const params = useParams()
  const { data: branchData } = useGetBranchDetail({
    branch: params.branch || '',
  })
  // firebase events
  useTransactionBranchSignal(params.branch || '')
  const [timeZoneReady, setTimeZoneReady] = useState(false)
  useEffect(() => {
    setTimeZoneReady(false)
    if (branchData?.timezone) {
      dayjs.tz.setDefault(branchData?.timezone)
      setTimeZoneReady(true)
    } else if (!params.branch) {
      dayjs.tz.setDefault(TIMEZONE[0].value)
      setTimeZoneReady(true)
    }
  }, [branchData, params.branch])
  const { isIpad } = useScreenWidth()
  const { setCollapsedSidebar, collapsedSidebar } = useCommonStore()
  const { menus, topMenus, haveOutStandingBilling } = useMenu()
  const navigate = useNavigate()
  const { isMobile } = useScreenWidth()
  const { myBranches } = useBranchStore()
  const location = useLocation()
  const activeMenu = useMemo(() => {
    const activePath = getOriginUrlWithoutBranch(location.pathname)
    if (remapActive[activePath]) {
      return remapActive[activePath]
    }
    return activePath
  }, [location.pathname])

  const haveSidebar = useMemo(() => {
    const matchUrl = pageUrlWithoutSidebar.some(
      (el) => !!matchPath(el, location.pathname),
    )
    return !matchUrl
  }, [location.pathname])
  const haveMargin = useMemo(() => {
    const matchUrl = pageUrlWithoutMargin.some(
      (el) => !!matchPath(el, location.pathname),
    )
    return !matchUrl
  }, [location.pathname])

  const collapsed = isIpad ? true : collapsedSidebar

  return (
    <>
      <Layout className="min-h-screen" hasSider={haveSidebar}>
        {haveSidebar && (
          <Sider
            trigger={
              isMobile && !collapsed ? (
                <div className="fixed bottom-0 right-0 z-30 w-screen h-screen bg-black/50"></div>
              ) : isIpad ? null : undefined
            }
            breakpoint="lg"
            collapsedWidth={isMobile ? 0 : undefined}
            collapsible
            id="sidebar"
            collapsed={collapsed}
            onCollapse={(value) => setCollapsedSidebar(value)}
          >
            <div className="p-2 mt-2">
              <Select
                suffixIcon={collapsed ? null : undefined}
                value={params.branch || getBranchLocalStorage()}
                className="w-full select-branch"
                size="large"
                onChange={(value) => {
                  saveBranch(value)
                  navigate(pageUrl.home.replace(':branch', value))
                }}
              >
                {myBranches.map((el) => (
                  <Select.Option value={el.slug} key={el.slug}>
                    <div
                      className={`flex items-center gap-2 ${
                        collapsed && 'justify-center ml-2'
                      }`}
                    >
                      <Avatar size="small" shape="square" src={`${el.logoUrl}`}>
                        {getInitial(el.name)}
                      </Avatar>{' '}
                      {!collapsed && (
                        <Text
                          ellipsis={{ tooltip: el.name }}
                          className="flex-1"
                          style={{ maxWidth: '190px' }}
                        >
                          {el.name}
                        </Text>
                      )}
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </div>
            <Menu
              disabled={haveOutStandingBilling}
              selectedKeys={[activeMenu]}
              theme="dark"
              mode="inline"
              items={[...menus, ...(isMobile ? topMenus : [])]}
              onClick={() => {
                isMobile && setCollapsedSidebar(!collapsed)
              }}
            />
          </Sider>
        )}

        <Layout>
          <Header className="flex items-center justify-between px-4 bg-white h-[42px] lg:h-[64px]">
            <Link to={pageUrl.branch} className="flex items-center">
              {isMobile ? (
                <img src={logoWithName} className="h-6" />
              ) : (
                <img src={logo} className="w-10" />
              )}
            </Link>
            {!isMobile && (
              <Menu
                mode="horizontal"
                items={topMenus}
                className="justify-end flex-1"
              />
            )}
            {isMobile && haveSidebar && (
              <Button
                size="middle"
                onClick={() => setCollapsedSidebar(!collapsed)}
                icon={<MenuOutlined />}
              />
            )}
          </Header>
          <Content
            className={classNames({
              'm-4': haveMargin,
              'p-0': !haveMargin,
            })}
          >
            <Suspense
              fallback={
                <div className="fixed grid w-full h-full place-content-center">
                  <Spin />
                </div>
              }
            >
              <div
                className={classNames({
                  'ml-auto mr-auto max-w-[1400px]': haveMargin,
                })}
              >
                {!!timeZoneReady && <Outlet />}
              </div>
            </Suspense>
          </Content>
          {/* <Footer style={{ textAlign: 'center' }}>
            Dashboard ©2023 Created by Glossify
          </Footer> */}
        </Layout>
      </Layout>
    </>
  )
}
